import { ACTION_TYPE, EVENT_NAMES, ELEMENT_TYPE, LAYOUT_TYPE } from '@/constants';
import { onMounted, onUnmounted, toRaw, watch, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { EditorCanvas } from '@/modules/editor/canvas/EditorCanvas.class';
import { useProjectStore, useUserStore } from '@/store';
import { createAction, createBookPage } from '@/modules/editor/canvas/models/creators';
import { canvasToFile, emitter } from '@/utils';
import { projectManager } from '@/managers';
import { uploadFile } from '@/api/upload';
import type { BookPage } from '@/modules/editor/canvas/book';
import type { BookPageData } from '@/types';
import { PlayAction } from '@/modules/editor/timeline/action';
import type { Element } from '@/modules/editor/canvas/element';
import { TextElement } from '@/modules/editor/canvas/element';
import { ElMessage } from 'element-plus';
import {
  consumeContentEditorReturnUrl,
  getContentEditorPreviewUrl
} from '@/utils/contentEditorReturn';
import { createSoundActionData } from '@/modules/editor/canvas/models/template/dataTemplate';

export const useEditor = (canvasRef: Ref<HTMLDivElement>) => {
  const canvas = new EditorCanvas();
  const projectStore = useProjectStore();
  const userStore = useUserStore();
  const route = useRoute();

  /** 存储 watch 返回的取消监听函数 */
  let unwatchCurrentPage: (() => void) | null = null;

  onMounted(async () => {
    await initCanvas();
    // HMR 热更新可能在 init() 异步过程中触发 destroy() 将 app 置为 null，
    // 此时 canvas 并未完整初始化，后续 initEvents / projectManager.open 会
    // 访问未初始化的 elementContainer 等成员导致崩溃，必须直接中止
    if (!canvas.getApp()) return;
    initEvents();
    await projectManager.open(route.params.id as string);
    redirectToPreviewIfUserMismatch();
  });

  onUnmounted(() => {
    // 先清空项目 store 数据
    projectManager.clear();
    destroy();
  });

  const initCanvas = async () => {
    await canvas.init(canvasRef.value);
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      projectManager.save();
    }
  };

  const initEvents = () => {
    window.addEventListener('keydown', handleKeydown);

    canvas.onSelect = (element: Element | null) => {
      projectStore.setCurrentAction(element ? element.id : '');
    };

    canvas.onElementUpdate = (element: Element | null) => {
      if (!element) return;
      // 变换/拖拽结束后，将元素数据同步回 store
      projectStore.updateElementData(element.id, element.data);
    };

    emitter.on(EVENT_NAMES.PROJECT_LOAD, async (params) => {
      // 后续还会有加载背景，bgm等其他操作，所以这里直接调用store里面的数据加载
      const { resolve } = params;

      // 切换工程时先清空旧页面
      canvas.clearElements();

      if (projectStore.bgm) {
        const bgmAc = await createAction(projectStore.bgm);
        if (bgmAc && bgmAc instanceof PlayAction) canvas.addBgm(bgmAc);
      }

      //先加载底页，在加载元素
      await addPages(projectStore.pages);

      syncPageThumbnails();

      resolve?.();
    });

    emitter.on(EVENT_NAMES.BOOK_PAGE_ADD, (params) => {
      const { pages, resolve } = params;

      addPages(pages);

      resolve?.();
    });

    emitter.on(EVENT_NAMES.BOOK_PAGE_REMOVE, (params) => {
      const { ids } = params;

      ids.forEach((id: string) => {
        canvas.removePage(id);
      });
    });

    // 更新 canvas page 中 action 对应的 element：存在则 reload，不存在则用 store 数据重建
    const reloadOrCreateCanvasAction = async (storeAction: any, page: BookPage) => {
      if (!storeAction?.element) return;
      const canvasAction = page.actions.get(storeAction.id);
      const element = canvasAction?.element;
      if (element) {
        (element.data as any).content = storeAction.element.content;
        await element.reload();
      } else {
        const newAction = await createAction(storeAction);
        if (newAction) {
          page.addAction(newAction);
        }
      }
    };

    emitter.on(EVENT_NAMES.ACTION_RELOAD, async (params) => {
      const { actionId, content } = params;

      const pageData = projectStore.currentPage;
      const actionData = pageData?.actions?.find((a) => a.id === actionId);
      if (!actionData) return;

      const oldContent = actionData.element?.content as string | undefined;

      // 1. 更新当前页 store
      if (actionData.element) {
        actionData.element.content = content;
        if (actionData.type !== ACTION_TYPE.SOUND) {
          projectStore.updateFileOption(
            actionData.id,
            content,
            actionData.element.relativePath ?? ''
          );
        }
      }

      // 2. FULL_SCREEN 跨页共用：优先用 page key 匹配，退化为 URL 匹配
      if (
        actionData.type !== ACTION_TYPE.SOUND &&
        projectStore.layout === LAYOUT_TYPE.FULL_SCREEN
      ) {
        let pairedAction: any;
        let otherPageData: any;

        if (pageData?.key) {
          // 新项目：通过跨页 key 找配对页
          otherPageData = projectStore.pages.find(
            (p) => p.id !== pageData!.id && p.key === pageData!.key
          );
          if (otherPageData) {
            pairedAction = otherPageData.actions?.find(
              (a: any) => a.element?.type === ELEMENT_TYPE.IMAGE
            );
          }
        } else if (oldContent && typeof oldContent === 'string') {
          // 旧项目：通过图片 URL 找配对页
          for (const p of projectStore.pages) {
            if (p.id === pageData?.id) continue;
            pairedAction = p.actions?.find(
              (a: any) =>
                a.id !== actionId &&
                a.element?.type === ELEMENT_TYPE.IMAGE &&
                a.element.content === oldContent
            );
            if (pairedAction) {
              otherPageData = p;
              break;
            }
          }
        }

        if (pairedAction?.element && otherPageData) {
          pairedAction.element.content = content;
          projectStore.updateFileOption(
            pairedAction.id,
            content,
            pairedAction.element.relativePath ?? ''
          );

          const pairedPage = canvas.getPages().find((p: any) => p.pageId === otherPageData.id);
          if (pairedPage) {
            await reloadOrCreateCanvasAction(pairedAction, pairedPage);
          }
        }
      }

      // 3. 更新当前页 canvas
      const page = canvas.getCurrentPage();
      if (page) {
        await reloadOrCreateCanvasAction(actionData, page);
      }

      projectStore.isModify = true;
      projectStore.updateCoverModify(true);
    });

    emitter.on(EVENT_NAMES.ACTION_ADD, async (actionData) => {
      if (!actionData) return;

      // 1. 添加到 store 当前页
      projectStore.addPageAction(actionData);

      // 2. 创建 canvas action 并加入当前页
      const action = await createAction(actionData);
      if (action) {
        canvas.addPageAction(action);
      }

      projectStore.isModify = true;
      projectStore.updateCoverModify(true);
    });

    emitter.on(EVENT_NAMES.ELEMENT_UPDATE, (elementData) => {
      // 项目加载期间跳过属性更新：此时 store 数据来自服务端加载，已是正确值，
      // 不需要同步；EditorPropertyBar 初始化时子组件 v-model 可能触发多余的 emit
      if (projectStore.isLoading) return;

      // 1. 先更新 store 当前页数据
      const pageData = projectStore.currentPage;
      const actionData = pageData?.actions?.find((a) => a.element?.id === elementData.id);
      if (actionData) {
        actionData.element = elementData;
      }

      // 2. 再更新 canvas 当前页中的 text element
      const currentPage = canvas.getCurrentPage();
      if (!currentPage) return;
      for (const action of currentPage.actions.values()) {
        if (action.element?.id === elementData.id) {
          const el = action.element;
          el.data = elementData;
          if (el instanceof TextElement) {
            el.refreshKeepCenter();
          } else {
            el.refresh();
          }

          canvas.updateTransformer();
          projectStore.isModify = true;
          projectStore.updateCoverModify(true);
          return;
        }
      }
    });

    emitter.on(EVENT_NAMES.APPLY_ALL_TEXTSTYLE, (textStyle) => {
      if (projectStore.isLoading) return;

      // 1. 更新 store 中所有页面的所有文本元素样式（跳过封面页和封底页）
      for (const pageData of projectStore.pages) {
        if (pageData.type === 'cover' || pageData.type === 'backCover') continue;
        for (const actionData of pageData.actions ?? []) {
          const { element } = actionData;
          const content = element?.content;
          if (
            element?.type === ELEMENT_TYPE.TEXT &&
            content &&
            typeof content !== 'string' &&
            'style' in content
          ) {
            content.style = { ...textStyle };
          }
        }
      }

      // 2. 更新 canvas 中所有已加载页面的文本元素并刷新（跳过封面页和封底页）
      for (const page of canvas.getPages()) {
        if (page.data?.type === 'cover' || page.data?.type === 'backCover') continue;
        for (const action of page.actions.values()) {
          const element = action.element;
          if (!element || element.data.type !== ELEMENT_TYPE.TEXT) continue;
          const content = element.data.content;
          if (content && typeof content !== 'string' && 'style' in content) {
            content.style = { ...textStyle };
            (element as TextElement).refreshKeepCenter();
          }
        }
      }
      canvas.updateTransformer();

      projectStore.isModify = true;

      projectStore.updateCoverModify(true);
    });

    emitter.on(EVENT_NAMES.BGM_ADD, async (bgmData) => {
      if (!bgmData || !bgmData.url) {
        if (projectStore.bgm) {
          projectStore.updateFileOption(
            projectStore.bgm.id,
            '',
            projectStore.bgm.element?.relativePath ?? ''
          );
        }
        projectStore.removeBgm();
        canvas.removeBgm();
        return;
      }

      const { url, name, volume } = bgmData;

      // 已有 bgm 则原地更新，否则新建
      const bgmActionData =
        projectStore.bgm ?? createSoundActionData(url, name, { volume, loop: true, isBgm: true });
      if (!projectStore.bgm) {
        bgmActionData.element!.relativePath = '';
      }

      bgmActionData.element!.content = url;
      bgmActionData.element!.name = name;
      const cfg = bgmActionData.element!.config ?? (bgmActionData.element!.config = {});
      cfg.volume = volume;
      cfg.loop = true;

      projectStore.updateFileOption(
        bgmActionData.id,
        url,
        projectStore.bgm?.element?.relativePath ?? ''
      );

      projectStore.addBgm(bgmActionData);

      canvas.removeBgm();
      const bgmAc = await createAction(bgmActionData);
      if (bgmAc && bgmAc instanceof PlayAction) {
        canvas.addBgm(bgmAc);
      }
    });

    emitter.on(EVENT_NAMES.BOOK_COVER_SYNC, async ({ resolve }) => {
      try {
        const firstPage = canvas.getFirstPage();

        if (!firstPage) {
          resolve(null);
          return;
        }

        const thumbnailCanvas = canvas.extractPageThumbnail(firstPage.pageId);
        if (!thumbnailCanvas) {
          resolve(null);
          return;
        }

        const file = await canvasToFile(thumbnailCanvas, 'cover.png');
        const url = await uploadFile(file);
        resolve(url);
      } catch (error) {
        console.warn('[useEditor] 封面截图上传失败:', error);
        resolve(null);
      }
    });

    emitter.on(EVENT_NAMES.TEST, () => {
      canvas.updatePlayActions();
      canvas.initTimeline();
      canvas.play();
    });
  };

  const redirectToPreviewIfUserMismatch = () => {
    const bookUserId = projectManager.getCurrentProjectUserId();
    const userId = Number(userStore.userData.userId);
    if (!bookUserId || !Number.isFinite(userId) || userId <= 0 || userId === bookUserId) return;

    const previewUrl =
      getContentEditorPreviewUrl(route.query.previewUrl) || consumeContentEditorReturnUrl();
    ElMessage.warning('当前登录用户不是该绘本所属用户，不允许编辑');
    if (!previewUrl) {
      if (window.history.length > 1) {
        window.history.back();
        return;
      }

      window.location.href = '/create';
      return;
    }

    window.location.href = previewUrl;
  };

  const addPages = async (pageDatas: BookPageData[]) => {
    if (!pageDatas || pageDatas.length === 0) return;

    const pageTemps: BookPage[] = [];

    pageDatas.forEach((data) => {
      // toRaw 获取原始数据生成pixijs相关对象，避免Proxy 开销叠加到每帧
      // 后续数据pixijs对象数据修改使用onElementUpdate同步回store
      const page = createBookPage(toRaw(data));
      pageTemps.push(page);
      page.updateMode(data.type === 'backCover' ? 'view' : 'edit');
      canvas.addPage(page);
    });

    await Promise.all(pageTemps.map((page) => page.load()));

    if (projectStore.enable_watermark) {
      canvas.setPageWatermark(true);
      canvas.hidePageWatermark();
    }
    canvas.setCurrentPage(projectStore.currentPageId);
  };

  /**
   * 同步单个页面的缩略图到侧边栏列表
   */
  const syncPageThumbnail = (pageId: string) => {
    try {
      const sourceCanvas = canvas.extractPageThumbnail(pageId);
      if (sourceCanvas) {
        emitter.emit(EVENT_NAMES.BOOK_PAGE_THUMB_SYNC, { pageId, sourceCanvas });
      }
    } catch (e) {
      console.warn(`[useEditor] 提取页面缩略图失败 (${pageId}):`, e);
    }
  };

  /**
   * 同步所有已加载页面的缩略图
   */
  const syncPageThumbnails = () => {
    projectStore.pages.forEach((p) => syncPageThumbnail(p.id));
  };

  const destroy = () => {
    removeEvents();

    // 清除 canvas 回调引用（重置为空函数，避免持有闭包引用）
    canvas.onSelect = null;
    canvas.onElementUpdate = null;

    // 销毁 canvas（释放 PixiJS 资源：stage、transformer、filters 等）
    canvas.destroy();

    // 停止 watch 监听
    unwatchCurrentPage?.();
  };

  const removeEvents = () => {
    window.removeEventListener('keydown', handleKeydown);

    emitter.off(EVENT_NAMES.PROJECT_LOAD);
    emitter.off(EVENT_NAMES.BOOK_PAGE_ADD);
    emitter.off(EVENT_NAMES.BOOK_PAGE_REMOVE);
    emitter.off(EVENT_NAMES.ACTION_RELOAD);
    emitter.off(EVENT_NAMES.ACTION_ADD);
    emitter.off(EVENT_NAMES.ELEMENT_UPDATE);
    emitter.off(EVENT_NAMES.APPLY_ALL_TEXTSTYLE);
    emitter.off(EVENT_NAMES.BGM_ADD);
    emitter.off(EVENT_NAMES.BOOK_COVER_SYNC);
    emitter.off(EVENT_NAMES.TEST);
  };

  const getCanvas = () => canvas;

  unwatchCurrentPage = watch(
    () => projectStore.currentPageId,
    (value, oldValue) => {
      if (oldValue) syncPageThumbnail(oldValue);
      canvas.setCurrentPage(value);
    }
  );

  return { getCanvas };
};
